import { useState } from "react";
import { accentClasses, type Accent } from "../../toolRegistry";

/**
 * Person avatar: a profile photo when one is set, initials otherwise.
 *
 * Single component so photos can be introduced one person at a time -- set
 * `photo` on their Collection and every place they appear picks it up. Falls
 * back to initials if the image 404s, so a typo in a filename degrades to a
 * tidy circle rather than a broken-image icon.
 */

const SIZES = {
  sm: "w-9 h-9 text-[12px]",
  md: "w-11 h-11 text-[14px]",
  lg: "w-14 h-14 text-[17px]",
} as const;

export default function Avatar({
  initials,
  accent,
  photo,
  size = "md",
  shape = "circle",
  alt,
}: {
  initials: string;
  accent: Accent;
  photo?: string;
  size?: keyof typeof SIZES;
  /** Rounded square reads as a profile tile next to text; circle suits inline use. */
  shape?: "circle" | "rounded";
  alt?: string;
}) {
  const [broken, setBroken] = useState(false);
  const a = accentClasses[accent];
  const dims = SIZES[size];
  const corners = shape === "rounded" ? "rounded-sm2" : "rounded-full";

  if (photo && !broken) {
    return (
      <img
        src={photo}
        alt={alt ?? initials}
        onError={() => setBroken(true)}
        loading="lazy"
        decoding="async"
        className={`${dims} ${corners} shrink-0 object-cover bg-paper-2`}
      />
    );
  }

  return (
    <span
      aria-label={alt}
      className={`${dims} ${corners} shrink-0 ${a.tint} ${a.text} flex items-center justify-center font-extrabold tracking-tight select-none`}
    >
      {initials}
    </span>
  );
}
